import { Box } from '@mui/material'
import React from 'react'

export type HeyaHeroVariant = 'flights' | 'hotels'

const HERO_CDN = 'https://ui.cltp.co/images/heya/hero'

// Both artworks are composed for Arabic — subjects on the inline-start side, which is the
// side the copy occupies in English — so the LTR mirror below is shared; only the sources
// differ.
const HERO_ARTWORK: Record<HeyaHeroVariant, { desktop: string; mobile: string }> = {
    flights: {
        desktop: `${HERO_CDN}/heya-hero-flights-desktop.webp`,
        mobile: `${HERO_CDN}/heya-hero-flights-mobile.webp`
    },
    hotels: {
        desktop: `${HERO_CDN}/heya-hero-hotels-desktop.webp`,
        mobile: `${HERO_CDN}/heya-hero-hotels-mobile.webp`
    }
}

/**
 * How far the photo extends below the hero copy, per product, in px: the gap down to the
 * search card plus half that card's initial height, so the photo finishes level with the
 * card's midline.
 *
 * Fixed rather than derived on purpose. The card's height is not stable — Multi City adds a
 * row and AI mode swaps the form out entirely — and the photo must not stretch with it.
 * Measured at 1280 and 1600, where both values hold.
 */
export const HEYA_HERO_BLEED: Record<HeyaHeroVariant, number> = {
    flights: 129,
    hotels: 149
}

interface HeyaHeroBackgroundProps {
    /**
     * Breakpoints this instance paints on. The hero spans the greeting and the search tabs,
     * which live in different components per breakpoint, so each caller claims its own.
     */
    display: Record<string, string>
    variant?: HeyaHeroVariant
    /** px to extend past the bottom of the band, so the photo can run under the search card. */
    bleed?: number
}

/**
 * Heya hero backdrop: photo layer, the legibility scrim under the copy, and the mobile fade
 * into the page below it.
 *
 * Callers render this only under the Heya theme, so it holds no brand check of its own —
 * it renders in both a server and a client tree, where neither `next/headers` nor a hook
 * would work everywhere.
 *
 * IMPORTANT — no percentage colour stops in any gradient added here. `stylis-plugin-rtl`
 * rewrites stop percentages as `100 - x` for the RTL cache but keeps their original order,
 * so `(0%, 34%, 58%)` becomes `(100%, 66%, 42%)`. CSS then clamps each stop to the largest
 * preceding one, collapsing the gradient to its first colour — which once rendered as a
 * flat white sheet over the whole hero in Arabic. Gradients with no percentages are immune;
 * `to right` is flipped to `to left` for Arabic, which is what the scrim wants.
 */
export const HeyaHeroBackground: React.FC<HeyaHeroBackgroundProps> = ({ display, variant = 'flights', bleed = 0 }) => {
    const artwork = HERO_ARTWORK[variant]
    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: `-${bleed}px`,
                overflow: 'hidden',
                display
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    // This is the LCP element on the pages it appears on. The CDN WebPs are
                    // sharp quality 75: desktop 2880×810 (2× the 1440 band), mobile 1290×540
                    // (3× a 430 phone), so neither is upscaled on retina screens.
                    //
                    // Quoted because emotion silently drops the whole declaration for an
                    // unquoted url() containing commas or parens.
                    backgroundImage: {
                        xs: `url("${artwork.mobile}")`,
                        lg: `url("${artwork.desktop}")`
                    },
                    backgroundSize: 'cover',
                    // Written for LTR: stylis-plugin-rtl rewrites the percentage as 100 - x
                    // for Arabic, which keeps the same edge of the photo against the copy.
                    backgroundPosition: { xs: '82% top', lg: 'center' },
                    backgroundRepeat: 'no-repeat',
                    // Mirrored for English so the subjects sit opposite the copy; left as shot
                    // for Arabic, which the artwork was composed for. Known cost: this reverses
                    // the Arabic هي wordmark painted into the flights photo. Accepted until
                    // design supplies an LTR-safe export — drop this rule to undo it.
                    'html[dir="ltr"] &': {
                        transform: 'scaleX(-1)'
                    },
                    maxHeight: "450px"
                }}
            />

            {/* Legibility scrim under the copy. Desktop holds the dark band across the copy
                column, then clears so the photo shows; it ends in transparent black, not white,
                to avoid a grey haze. No percentage stops — see the note above. */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: {
                        xs: 'linear-gradient(to right, rgba(0, 0, 0, 0.96), rgba(0, 0, 0, 0.39), rgba(255,255,255,0))',
                        lg: 'linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0))'
                    }
                }}
            />

            {/* Mobile: fade the photo into the page below it. Direction-agnostic, so Arabic
                needs no special case here. */}
            <Box
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgb(255, 255, 255))'
                }}
            />
        </Box>
    )
}
